<template>
    <div>
        <Map :layers="layers"></Map>
        <div v-if="basinsMonthsLayer" class="legend-ct">
            <h5>{{basinsMonthsLayer.classification.title}}</h5>
            <div><span class="color-box color1"></span> &lt; 0.25 (low)</div>
            <div><span class="color-box color2"></span> &ge; 0.25 (low)</div>
            <div><span class="color-box color3"></span> &ge; 0.5 (low)</div>
            <div><span class="color-box color4"></span> &ge; 1 (moderate)</div>
            <div><span class="color-box color5"></span> &ge; 1.5 (significant)</div>
            <div><span class="color-box color6"></span> &ge; 2 (severe)</div>
            <div><span class="color-box color7"></span> &ge; 4 (severe)</div>
        </div>
    </div>
</template>

<script>
import { CenterScale, Filter, FocusModel, Geometry, Layer, WKTConverter, defaultFocusModel, windowBoundsModel } from "vue-niney";
import { assessment } from "@/services/assessment-model.js";
import Map from '@/components/shared/Map.vue'
export default {
    components: {
        Map
    },
    mounted: function() {
        if (assessment.countriesOrBasins.length > 0) {
            const geometry = new Geometry();
            for (const countryOrBasin of assessment.countriesOrBasins) {
                geometry.addChild((new WKTConverter()).wktToGeometry(countryOrBasin.envelope));
            }
            
            const envelope = geometry.getEnvelope();
            const centerX = envelope.minX + envelope.getWidth() / 2;
            const centerY = envelope.minY + envelope.getHeight() / 2;
            const scale = Math.max(envelope.getWidth() / windowBoundsModel.bounds.width, envelope.getHeight() / windowBoundsModel.bounds.height) / defaultFocusModel.centerScale.coordPixFactor * 1.05;
            defaultFocusModel.setCenterScale(new CenterScale(centerX, centerY, scale), FocusModel.IF_REQUIRED_UPPER);
        }
    },
    computed: {
        rasterCropsLayer: function() {
            const rasterCropsClassifications = {
                green: { propertyName: "raster_theme(112, ", title: "Green water footprint", unit: "mm/yr", colors: [0xAAFFAA, 0x006000], numbers: null, gradient: true, discrete: false, numClasses: 6 },
                blue: { propertyName: "raster_theme(113, ", title: "Blue water footprint", unit: "mm/yr", colors: [0xB0D9FF, 0x004281], numbers: null, gradient: true, discrete: false, numClasses: 6 },
                grey: { propertyName: "raster_theme(114, ", title: "Grey water footprint", unit: "mm/yr", colors: [0xCCCCCC, 0x4D4D4D], numbers: null, gradient: true, discrete: false, numClasses: 6 },
                total: { propertyName: "raster_theme(1008, ", title: "Water footprint", unit: "mm/yr", colors: [0xEEEE00, 0xDD0000], numbers: null, gradient: true, discrete: false, numClasses: 6 }
            };
            
            const layer = new Layer("RasterCrops");
            layer.baseURL = "https://www.waterfootprintassessmenttool.org/nessie/raster.jsp";
            layer.styleURL = "https://www.waterfootprintassessmenttool.org/nessie/named-layer-sld-4-raster.jsp";
            layer.filterModels = [{ filter: new Filter("app:cropID", (assessment.crop != null)? assessment.crop.cropID: 100001) }];
            layer.classification = rasterCropsClassifications[assessment.viewColor];
            
            return layer;
        },
        basinsMonthsLayer: function() {
            if (assessment.step.index >= 2) {
                const basinsMonthsClassifications = {
                    blue: { propertyName: "blueScarcity", title: "Blue Water Scarcity", unit: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0.25, 0.5, 1.0, 1.5, 2.0, 4.0] },
                    grey: { propertyName: "greyWS", title: "Water Pollution Level", unit: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0.25, 0.5, 1.0, 1.5, 2.0, 4.0] }
                }
                
                const layer = new Layer("BasinsMonths");
                layer.baseURL = "https://www.waterfootprintassessmenttool.org/nessie/services";
                layer.styleURL = "https://www.waterfootprintassessmenttool.org/nessie/named-layer-sld.jsp";
                layer.filterModels = [{ filter: new Filter("app:monthID", 101) }];
                layer.classification = basinsMonthsClassifications[assessment.viewColor];
                
                return layer;
            } else {
                return null;
            }
        },
        basinsTilesLayer: function() {
            if (assessment.step.index >= 2) {
                const layer = new Layer("BasinsTiles");
                layer.baseURL = "https://www.waterfootprintassessmenttool.org/tiles/basins/";
                
                return layer;
            } else {
                return null;
            }
        },
        countriesOrBasinsLayer: function() {
            if (assessment.countriesOrBasins.length > 0) {
                const idPropertyName = assessment.assessmentType.idPropertyName;
                const ids = [];
                for (const countryOrBasin of assessment.countriesOrBasins) {
                    ids.push(countryOrBasin[idPropertyName]);
                }
                
                const layer = new Layer(assessment.assessmentType.layerName);
                layer.baseURL = "https://www.waterfootprintassessmenttool.org/nessie/services";
                layer.styleURL = "https://www.waterfootprintassessmenttool.org/nessie/named-layer-sld.jsp";
                layer.filterModels = [{ filter: new Filter("app:" + idPropertyName, ids.join(":")) }];
                
                return layer;
            } else {
                return null;
            }
        },
        layers: function() {
            return [this.rasterCropsLayer, this.basinsMonthsLayer, this.basinsTilesLayer, this.countriesOrBasinsLayer];
        },
        step: function() {
            return assessment.step.name;
        },
    }
}
</script>

<style lang="scss" scoped>
.legend-ct {
    padding: 10px;
    background-color: rgba(237, 235, 229, 0.5);
    border-radius: 10px;
    position: absolute;
    bottom: 10px;
    left: 10px;
    h5{
        font-size: 15px;
        font-weight: 700;
    }
    .color-box {
        width: 20px;
        height: 12px;
        margin-left: 10px;
        display: inline-block;
        &.color1 {
            background-color: #115511;
        }
        &.color2 {
            background-color: #5b880b;
        }
        &.color3 {
            background-color: #a4bb06;
        }
        &.color4 {
            background-color: #eeee00;
        }
        &.color5 {
            background-color: #e89f00;
        }
        &.color6 {
            background-color: #e34f00;
        }
        &.color7 {
            background-color: #dd0000;
        }
    }
    
}
</style>
